/** Staff (employee) endpoints (`/staff`, management only). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getData, getList, patchData, postData } from "../lib/apiClient";
import { queryKeys } from "./queryKeys";
import { Role, StaffProfile } from "../types/models";
import { ListParams } from "../types/api";

export interface ListStaffParams extends ListParams {
  department?: string;
  isActive?: boolean;
}

export interface CreateStaffInput {
  fullName: string;
  employeeCode?: string;
  designation?: string;
  department?: string;
  phone?: string;
  joiningDate: string; // ISO date
  monthlySalary: number;
  /** Optional portal login. When `email` is set, `password` + `role` are required. */
  email?: string;
  password?: string;
  role?: Extract<Role, "MANAGER" | "STORE_KEEPER" | "PRODUCTION_SUPERVISOR">;
}

export interface UpdateStaffInput {
  fullName?: string;
  designation?: string;
  department?: string;
  phone?: string;
  joiningDate?: string;
  monthlySalary?: number;
  isActive?: boolean;
}

export const staffApi = {
  list: (params?: ListStaffParams) => getList<StaffProfile>("/staff", params),
  getById: (id: string) => getData<StaffProfile>(`/staff/${id}`),
  create: (input: CreateStaffInput) => postData<StaffProfile>("/staff", input),
  update: (id: string, input: UpdateStaffInput) =>
    patchData<StaffProfile>(`/staff/${id}`, input),
};

// ── Hooks ────────────────────────────────────────────────────────────

export const useStaff = (params?: ListStaffParams) =>
  useQuery({
    queryKey: queryKeys.staff.list(params),
    queryFn: () => staffApi.list(params),
  });

export const useStaffMember = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.staff.detail(id ?? ""),
    queryFn: () => staffApi.getById(id as string),
    enabled: Boolean(id),
  });

export const useCreateStaff = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStaffInput) => staffApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.staff.all }),
  });
};

export const useUpdateStaff = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateStaffInput }) =>
      staffApi.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.staff.all }),
  });
};
